import React, { useState } from 'react';
import { MemberRegistrationForm, MembershipType } from '../../types';

interface CodeOfConductStepProps {
  formData: MemberRegistrationForm;
  updateFormData: (updates: Partial<MemberRegistrationForm>) => void;
  onNext: () => void;
  onBack: () => void;
  onNavigateToRules?: () => void;
  onNavigateToConduct?: () => void;
  onNavigateToWaiver?: () => void;
}

const CodeOfConductStep: React.FC<CodeOfConductStepProps> = ({
  formData,
  updateFormData,
  onNext,
  onBack,
  onNavigateToRules,
  onNavigateToConduct,
  onNavigateToWaiver,
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [aiProcessingAccepted, setAiProcessingAccepted] = useState(false);
  const [photoUseAccepted, setPhotoUseAccepted] = useState(false);

  const handleNext = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.clubRulesAccepted) {
      newErrors.clubRules = 'You must accept the Club Rules to continue';
    }
    if (!formData.codeOfConductAccepted) {
      newErrors.codeOfConduct = 'You must accept the Code of Conduct to continue';
    }
    if (!formData.waiverAccepted) {
      newErrors.waiver = 'You must accept the Waiver and Release to continue';
    }
    if (!aiProcessingAccepted) {
      newErrors.aiProcessing = 'You must acknowledge AI photo processing to continue';
    }
    if (!photoUseAccepted) {
      newErrors.photoUse = 'You must agree to the Photo and Image Use terms to continue';
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length === 0) {
      onNext();
    }
  };

  const isFamilyType = formData.membershipType === MembershipType.FAMILY ||
    formData.membershipType === MembershipType.SPONSOR_GOLD ||
    formData.membershipType === MembershipType.SPONSOR_SILVER ||
    formData.membershipType === MembershipType.SPONSOR_BRONZE;
  const isJunior = formData.membershipType === MembershipType.JUNIOR;

  const onBehalfSuffix = isFamilyType ? ' on behalf of myself and all family members included in this application' : '';

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Club Documents & Agreements</h2>
      <p className="text-gray-600 mb-6">
        Please review and accept the following club documents to complete your registration
      </p>

      {isFamilyType && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800 font-medium">
            📋 By accepting these documents, you are agreeing on behalf of yourself and all family members included in this application.
          </p>
        </div>
      )}

      <div className="space-y-6">
        {/* Club Rules */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Club Rules</h3>
          <p className="text-gray-700 mb-4">
            Our club rules outline the regulations for membership, fishing competitions, 
            adjudication, prizes, and awards. Please review them carefully.
          </p>
          <button
            onClick={onNavigateToRules}
            className="text-fishing-blue hover:text-blue-700 font-medium mb-4 flex items-center"
          >
            📖 Read Club Rules →
          </button>
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={formData.clubRulesAccepted}
              onChange={(e) => {
                updateFormData({ clubRulesAccepted: e.target.checked });
                if (errors.clubRules) {
                  setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.clubRules;
                    return newErrors;
                  });
                }
              }}
              className="mt-1 mr-3 h-5 w-5 text-fishing-blue focus:ring-fishing-blue border-gray-300 rounded"
            />
            <span className="text-gray-700">
              I have read and agree to abide by the Tumbulgum Fishing Club Rules{onBehalfSuffix}
              <span className="text-red-500 ml-1">*</span>
            </span>
          </label>
          {errors.clubRules && <p className="text-red-500 text-sm mt-2">{errors.clubRules}</p>}
        </div>

        {/* Code of Conduct */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Code of Conduct</h3>
          <p className="text-gray-700 mb-4">
            Our Code of Conduct ensures a respectful, safe, and inclusive environment for all members. 
            It covers expected behavior and unacceptable conduct.
          </p>
          <button
            onClick={onNavigateToConduct}
            className="text-fishing-blue hover:text-blue-700 font-medium mb-4 flex items-center"
          >
            📋 Read Code of Conduct →
          </button>
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={formData.codeOfConductAccepted}
              onChange={(e) => {
                updateFormData({ codeOfConductAccepted: e.target.checked });
                if (errors.codeOfConduct) {
                  setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.codeOfConduct;
                    return newErrors;
                  });
                }
              }}
              className="mt-1 mr-3 h-5 w-5 text-fishing-blue focus:ring-fishing-blue border-gray-300 rounded"
            />
            <span className="text-gray-700">
              I have read and agree to abide by the Tumbulgum Fishing Club Code of Conduct{onBehalfSuffix}
              <span className="text-red-500 ml-1">*</span>
            </span>
          </label>
          {errors.codeOfConduct && <p className="text-red-500 text-sm mt-2">{errors.codeOfConduct}</p>}
        </div>

        {/* Waiver and Release */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Waiver and Release</h3>
          <p className="text-gray-700 mb-4">
            The waiver outlines the risks associated with fishing activities and releases the club 
            from liability. Please read it carefully before accepting.
          </p>
          <button
            onClick={onNavigateToWaiver}
            className="text-fishing-blue hover:text-blue-700 font-medium mb-4 flex items-center"
          >
            ⚠️ Read Waiver and Release →
          </button>
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={formData.waiverAccepted}
              onChange={(e) => {
                updateFormData({ waiverAccepted: e.target.checked });
                if (errors.waiver) {
                  setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.waiver;
                    return newErrors;
                  });
                }
              }}
              className="mt-1 mr-3 h-5 w-5 text-fishing-blue focus:ring-fishing-blue border-gray-300 rounded"
            />
            <span className="text-gray-700">
              {isJunior
                ? 'I, as the guardian, confirm the waiver on behalf of the junior member.'
                : `I have read and accept the Tumbulgum Fishing Club Waiver and Release${onBehalfSuffix}`}
              <span className="text-red-500 ml-1">*</span>
            </span>
          </label>
          {errors.waiver && <p className="text-red-500 text-sm mt-2">{errors.waiver}</p>}
        </div>

        {/* AI Photo Verification */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">AI Photo Verification</h3>
          <p className="text-gray-700 mb-4">
            Catch photos submitted during competitions are processed by AI for verification purposes (e.g. detecting photo tampering, species mismatches, and confirming competition stickers). The AI advisory score is used by committee adjudicators to help review entries.
          </p>
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={aiProcessingAccepted}
              onChange={(e) => {
                setAiProcessingAccepted(e.target.checked);
                if (errors.aiProcessing) {
                  setErrors((prev) => {
                    const copy = { ...prev };
                    delete copy.aiProcessing;
                    return copy;
                  });
                }
              }}
              className="mt-1 mr-3 h-5 w-5 text-fishing-blue focus:ring-fishing-blue border-gray-300 rounded"
            />
            <span className="text-gray-700">
              I acknowledge that catch photos will be processed by AI for verification purposes.
              <span className="text-red-500 ml-1">*</span>
            </span>
          </label>
          {errors.aiProcessing && <p className="text-red-500 text-sm mt-2">{errors.aiProcessing}</p>}
        </div>

        {/* Photo and Image Use */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Photo and Image Use</h3>
          <p className="text-gray-700 mb-4">
            Any photo you submit to the Club — including catch photos, Happy Snaps, and event photos — may be used by the Club for Club purposes, including on the Club's website, social media, and other online channels.
          </p>
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={photoUseAccepted}
              onChange={(e) => {
                setPhotoUseAccepted(e.target.checked);
                if (errors.photoUse) {
                  setErrors((prev) => {
                    const copy = { ...prev };
                    delete copy.photoUse;
                    return copy;
                  });
                }
              }}
              className="mt-1 mr-3 h-5 w-5 text-fishing-blue focus:ring-fishing-blue border-gray-300 rounded"
            />
            <span className="text-gray-700">
              I agree that photos I submit to the Club may be used for Club purposes, including online{onBehalfSuffix}.
              <span className="text-red-500 ml-1">*</span>
            </span>
          </label>
          {errors.photoUse && <p className="text-red-500 text-sm mt-2">{errors.photoUse}</p>}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between mt-8">
        <button
          onClick={onBack}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          className="px-6 py-2 bg-fishing-blue hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default CodeOfConductStep;
